import React from 'react';
import { Redirect, Route } from 'react-router-dom';
import appPages from 'app/appPages';
import withBanner from 'app/blocks/banner-messages/withBanner';
import { Layout } from 'app/blocks/blocks';
import { ID, withCodes2 } from 'app/blocks/common/codes';
import showDialog from 'app/blocks/common/jsx/dialogModal';
import { cleanToken, compose, getQueryParam, getQueryParamSafe } from 'app/blocks/common/utils';
import withAnalytics, { withAnalyticsContext } from 'app/blocks/common/withAnalytics';
import withAuthRequiredPage from 'app/blocks/common/withAuthRequiredPage';
import withScrollToTop from 'app/blocks/common/withScrollToTop';
import withTopMenu from 'app/blocks/common/withTopMenu';
import withUnauthRequiredPage from 'app/blocks/common/withUnauthRequiredPage';
import Message from 'app/blocks/message/message';
import { logout, refreshUser } from 'app/blocks/middleware/authentication';
import withUrlParams, { updateUrlParam } from 'app/blocks/middleware/url-params';
import { handleOrcidRedirect } from 'app/blocks/orcid/OrcidConnector';
import ArticlePage from 'app/pages/article';
import ArticleAssignmentByJournal from 'app/pages/assign-article/_by-journal/assign-article_by-journal';
import ArticleAssignmentById from 'app/pages/assign-article/article-assignment-by-id/article-assignment-by-id';
import ChangePrimaryEmail from 'app/pages/change-primary-email/change-primary-email';
import Dashboard from 'app/pages/dashboard';
import InviteColleague from 'app/pages/invite-colleague/invite-colleague';
import LicenseSigningProcessPage from 'app/pages/license-signing-process/LicenseSigningProcessPage';
import LoginPage from 'app/pages/login/login-page';
import RecoverPasswordPage from 'app/pages/login/recover-password-page';
import TermsOfServicePage from 'app/pages/login/terms-of-service-page';
import OrdersConfirmationHandler from 'app/pages/orders/confirmation-handler';
import OrdersOA, { OpenAccessOrderStepEnum } from 'app/pages/orders/oa';
import Orders from 'app/pages/orders/orders';
import OrdersRequestOnlineOpen from 'app/pages/orders/request-oo';
import OrdersRequestOOView from 'app/pages/orders/request-oo/view';
import ProfilePage from 'app/pages/profile/profile-page';
import PubEditorReview from 'app/pages/pubeditor-review/pubeditor-review';
import EmailVerificationPage from 'app/pages/registration/email-verification-page';
import RegistrationPage from 'app/pages/registration/registration-page';
import AdvanceContentSearch from 'app/pages/search/_advanced/advance_content_search';
import MissingArticleDetails from 'app/pages/search/_advanced/missing-article-details/MissingArticleDetails';
import ContentSearch from 'app/pages/search/content_search';
import Version from 'app/pages/version/version';
import { navigate } from 'app/route/history';
import tempStorage from 'app/route/tempStorage';
import EmailAlertsPage from '../pages/email-alerts/email-alerts-page';

const withNamedAuth = page => Component => withAuthRequiredPage(Component, page);
const withAuthForPage = page => compose(withBanner, withNamedAuth(page), withScrollToTop);
const withUnauth = compose(withBanner, withUnauthRequiredPage, withScrollToTop);

function HomeRoute() {
    window.location.replace('/home.html');

    return null;
}

const LoginRoute = withUnauth(props => (
    <LoginPage {...props} queryParams={getQueryParam(props.location.search)} skipNotify />
));

const RegistrationRoute = compose(withUnauth, withUrlParams)(props => <RegistrationPage {...props} />, true);

function RegistrationByInviteRoute(props) {
    updateUrlParam('invitationToken', cleanToken(props.match.params.token || ''));

    const { article } = getQueryParamSafe(props.location.search, true);
    if (article) {
        updateUrlParam('invitationArticleId', article);
    }

    return <RegistrationRoute {...props} />;
}

function CallbackRedirectRoute() {
    // RFC 3986 "syntax for URIs" states that query parameters should come before hash fragment.
    // So redirect from ORCID will be to /landing.html?code={code}#register/orcid

    let { hash, search } = window.location;

    if (hash.includes('?')) {
        // access_denied redirects to
        // /landing.html#register/orcid?error=access_denied&error_description=User denied access
        const arr = window.location.hash.split('?');
        // eslint-disable-next-line prefer-destructuring
        hash = arr[0];
        search = `?${arr[1]}`;
    }

    search = decodeURIComponent(search);

    window.location.replace(`${window.location.pathname}${hash}/${search}`);

    return null;
}

const RecoverPasswordRoute = compose(
    withAnalytics(appPages.RECOVER_PASSWORD),
    withUnauth,
)(props => <RecoverPasswordPage {...props} />);

const ProfileRoute = compose(
    withAnalyticsContext(appPages.PROFILE),
    withAuthForPage(appPages.PROFILE),
    withTopMenu,
)(props => <ProfilePage {...props} section={getQueryParam(props.location.search).section} />);

const VerifyEmailRoute = compose(
    withBanner,
    withScrollToTop,
)(props => <EmailVerificationPage {...props} verifyEmailUuid={cleanToken(props.match.params.token)} />);

const MessageRoute = compose(
    withBanner,
    withScrollToTop,
)(props => <Message {...props} {...getQueryParam(props.location.search)} />);

const ResetPasswordRoute = compose(
    withAnalytics(appPages.RESET_PASSWORD),
    withUnauth,
    withCodes2(ID.RECOVER_PASSWORD),
)(props => (
    <RecoverPasswordPage
        {...props}
        queryParams={getQueryParam(props.location.search)}
        uuid={encodeURIComponent(cleanToken(props.match.params.uuid))}
    />
));

const ChangePrimaryEmailRoute = compose(
    withAnalytics(appPages.CHANGE_PRIMARY_EMAIL),
    withBanner,
    withScrollToTop,
)(props => {
    logout(true);

    return <ChangePrimaryEmail {...props} guid={props.match.params.guid} />;
});

const DashboardRoute = compose(
    withAuthForPage(appPages.DASHBOARD),
    withTopMenu,
)(props => <Dashboard {...props} focusOnArticle={getQueryParam(props.location.search).focusOnArticle} />);

const ArticlePageRoute = withAuthForPage(appPages.ARTICLE_PAGE)(
    withTopMenu(props => <ArticlePage {...props} articleId={props.match.params.articleId} />),
);

const InviteColleagueRoute = withAuthForPage('INVITE_COLLEAGUE')(props => (
    <InviteColleague {...props} articleId={getQueryParam(props.location.search).articleId} />
));

const SearchRoute = compose(
    withBanner,
    withScrollToTop,
)(({ location }) => {
    const { text } = getQueryParam(location.search, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const context = React.useMemo(() => tempStorage.get(), [text]);

    return <ContentSearch key={text} context={context} text={text} />;
});

const AdvanceSearchRoute = compose(
    withBanner,
    withScrollToTop,
)(props => {
    const context = React.useMemo(() => tempStorage.get(), []);
    const { jid, type } = getQueryParam(props.location.search);

    return <AdvanceContentSearch {...props} key={`${jid}-${type}`} context={context} jid={jid} type={type} />;
});

const MissingArticleDetailsRoute = compose(
    withAnalytics(appPages.MISSING_ARTICLE_DETAILS),
    withAuthForPage(appPages.MISSING_ARTICLE_DETAILS),
)(props => <MissingArticleDetails {...props} />);

const TermsOfServiceRoute = compose(
    withAnalytics(appPages.TERMS_OF_SERVICE),
    withAuthForPage(appPages.TERMS_OF_SERVICE),
)(props => <TermsOfServicePage {...props} />);

const VersionRoute = withAnalytics(appPages.VERSION)(props => <Version {...props} />);

const EmailAlertsRoute = withAnalytics(appPages.EMAIL_ALERTS)(props => (
    <EmailAlertsPage {...props} {...getQueryParam(props.location.search)} />
));

const CheckAuthAndRedirectRoute = withCodes2(
    ID.ERROR,
    ID.LOGIN,
)(props => {
    const { link } = getQueryParam(props.location.search, true);
    const isLinkValid = link && link[0] === '/';

    const [isLoading, setLoading] = React.useState(true);

    React.useEffect(() => {
        if (isLinkValid) {
            (async () => {
                try {
                    const { loggedIn } = await refreshUser();

                    if (loggedIn) {
                        window.location.href = link;
                    } else {
                        setLoading(false);

                        showDialog.error({
                            message: props.l('ERROR.SESSION_HAS_BEEN_EXPIRED'),
                            closeBtnLabel: props.l('LOGIN.pageName'),
                            onClose() {
                                logout();
                            },
                        });
                    }
                } catch (error) {
                    setLoading(false);

                    showDialog.error(error);
                }
            })();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!isLinkValid) {
        return <Message message={props.l('ERROR.WRONG_REDIRECT_LINK')} />;
    }
    if (isLoading) {
        return <Layout isLoading />;
    }

    return null;
});

function OrcidRedirectRoute({ location }) {
    handleOrcidRedirect(getQueryParam(location.search));

    return null;
}

function DashboardCallbackRoute(props) {
    if (/[?&]message=/.test(props.location.search)) {
        const params = getQueryParam(props.location.search);
        const message = decodeURIComponent(params.message.replace(/\+/g, '%20'));
        const code = decodeURIComponent(params.code);
        const refId = decodeURIComponent(params.refId);

        return <Dashboard {...props} code={code} error={message} refId={refId} />;
    }

    return null;
}

const ReviewPDFRoute = withBanner(props => {
    if (/[?&]message=/.test(props.location.search)) {
        const params = getQueryParam(props.location.search);
        const message = decodeURIComponent(params.message.replace(/\+/g, '%20'));
        const code = decodeURIComponent(params.code);
        const refId = decodeURIComponent(params.refId);

        return <PubEditorReview {...props} code={code} error={message} refId={refId} />;
    }

    return null;
});

const PubEditorReviewRoute = compose(
    withAnalytics(appPages.PUB_EDITOR_REVIEW),
    withBanner,
)(({ location }) => {
    const props = getQueryParam(location.search);

    if (props.token) {
        props.token = cleanToken(props.token);
    }

    return <PubEditorReview {...props} />;
});

const LicenseSigningRoute = withBanner(props => {
    return (
        <LicenseSigningProcessPage
            articleId={props.match.params.articleId}
            step={props.match.params.step}
            substep={props.match.params.substep}
        />
    );
});

const OpenAccessConfirmationRoute = compose(
    withBanner,
    withNamedAuth(appPages.OPEN_ACCESS_CONFIRMATION),
)(() => <OrdersConfirmationHandler oa title="Open Access Payment" />);

const OrdersRoute = compose(
    withAnalyticsContext(appPages.ORDERS),
    withBanner,
    withNamedAuth(appPages.ORDERS),
    withTopMenu,
)(({ location }) => <Orders error={getQueryParam(location.search).error} />);

const OrdersFocusOnArticleRoute = compose(
    withAnalyticsContext(appPages.ORDERS_FOCUS_ON_ARTICLE),
    withBanner,
    withNamedAuth(appPages.ORDERS_FOCUS_ON_ARTICLE),
)(props => {
    return <Orders error={getQueryParam(props.location.search).error} focusOnArticle={props.match.params.articleId} />;
});

const OrderRoute = compose(
    withBanner,
    withNamedAuth(appPages.ORDERS),
)(props => {
    const { error, order } = React.useMemo(() => tempStorage.get(), []) || {};

    const params = getQueryParam(props.location.search);

    if (params.type === 'openaccess') {
        return (
            <OrdersOA
                action={params.action || OpenAccessOrderStepEnum.INITIATE}
                articleId={props.match.params.articleId}
                cpsError={params.status}
                error={error}
                loadedOrder={order}
                section={params.edit}
                socopayOrderNumber={params.socopayOrderNumber}
            />
        );
    }

    return null;
});

const RequestOnlineOpenOrderRoute = compose(
    withBanner,
    withNamedAuth(appPages.REQUEST_ONLINE_OPEN),
)(({ location }) => {
    const params = getQueryParam(location.search);

    if (params.type !== 'online-open') {
        return <Message message="Request Online Open for non Online Open type" />;
    }

    return <OrdersRequestOnlineOpen articleId={params.articleId} gotoDashboard={() => navigate('dashboard')} />;
});

const ViewOnlineOpenRequestRoute = compose(
    withBanner,
    withNamedAuth(appPages.VIEW_ONLINE_OPEN),
)(({ location }) => {
    const params = getQueryParam(location.search);

    if (params.type !== 'online-open') {
        return <Message message="Request Online Open for non Online Open type" />;
    }

    return <OrdersRequestOOView articleId={params.articleId} />;
});

function cancelArticleAssignment(context) {
    if (typeof context.query === 'string') {
        navigate(`search?text=${context.query}`, undefined, context);
    } else if (context.jid) {
        navigate(`advancedsearch?jid=${context.jid}&type=article`, undefined, context);
    } else {
        navigate(`advancedsearch?type=article`, undefined, context);
    }
}

const AssignArticleRoute = compose(
    withBanner,
    withNamedAuth(appPages.ASSIGN_ARTICLE),
)(({ location }) => {
    const params = getQueryParam(location.search);
    const context = React.useMemo(() => tempStorage.get(), []);
    let cancel;

    if (context) {
        cancel = () => cancelArticleAssignment(context);
    } else {
        cancel = () => navigate('dashboard');
    }

    if (params.articleId) {
        return (
            <ArticleAssignmentById
                articleId={params.articleId}
                cancel={cancel}
                gotoDashboard={() => navigate('dashboard')}
            />
        );
    }
    if (params.journalId) {
        return (
            <ArticleAssignmentByJournal
                cancel={cancel}
                gotoDashboard={() => navigate('dashboard')}
                journalId={params.journalId}
            />
        );
    }

    return null;
});

const NoMatchRoute = withBanner(() => <Redirect to="/dashboard" />);

export default [
    <Route key="Home" component={HomeRoute} exact path="/" />,
    <Route key="Login" component={LoginRoute} path="/login" />,
    <Route key="Registration" component={RegistrationRoute} exact path="/register" />,
    <Route key="RegistrationByInvite" component={RegistrationByInviteRoute} path="/register-invite/:token" />,
    <Route key="RecoverPassword" component={RecoverPasswordRoute} path="/recover" />,
    <Route key="Profile" component={ProfileRoute} exact path="/profile" />,
    <Route key="VerifyEmail" component={VerifyEmailRoute} path="/verify/:token" />,
    <Route key="Message" component={MessageRoute} path="/message" />,
    <Route key="ResetPassword" component={ResetPasswordRoute} path="/resetPassword/:uuid" />,
    <Route key="ChangePrimaryEmail" component={ChangePrimaryEmailRoute} path="/changePrimaryEmail/:guid" />,
    <Route key="Dashboard" component={DashboardRoute} exact path="/dashboard" />,
    <Route key="ArticlePage" component={ArticlePageRoute} path="/article/:articleId" />,
    <Route key="InviteColleague" component={InviteColleagueRoute} path="/invite-colleague" />,
    <Route key="Search" component={SearchRoute} path="/search" />,
    <Route key="Search" component={MissingArticleDetailsRoute} exact path="/get-help-on-article" />,
    <Route key="AdvanceSearch" component={AdvanceSearchRoute} path="/advancedsearch" />,
    <Route key="TermsOfService" component={TermsOfServiceRoute} path="/terms-of-service" />,
    <Route key="Version" component={VersionRoute} path="/version" />,
    <Route key="CheckAuthAndRedirect" component={CheckAuthAndRedirectRoute} path="/checkAuthAndRedirect" />,
    <Route
        key="CallbackRedirectRoute"
        component={CallbackRedirectRoute}
        exact
        path={[
            `/orcid-integration/orcid`,
            '/dashboard/articlePdf',
            '/dashboard/invoicePdf',
            '/license/licenseCopyPdf',
            '/license/printLicensePdf',
            '/license/verification',
            '/openaccess/confirmation',
            '/profile/orcid',
            '/register/orcid',
        ]}
        strict
    />,
    // <Route
    //     key="CallbackRedirectDecodeURI"
    //     component={CallbackRedirectDecodeURIRoute}
    //     exact
    //     path="/license/verification"
    //     strict
    // />,
    <Route
        key="OrcidRedirect"
        component={OrcidRedirectRoute}
        exact
        path={['/register/orcid/', '/profile/orcid/', `/orcid-integration/orcid/`]}
        strict
    />,
    <Route
        key="OpenAccessConfirmation"
        component={OpenAccessConfirmationRoute}
        exact
        path="/openaccess/confirmation/"
        strict
    />,
    <Route
        key="DashboardCallback"
        component={DashboardCallbackRoute}
        exact
        path={['/dashboard/articlePdf/', '/dashboard/invoicePdf/', '/license/printLicensePdf/']}
        strict
    />,
    <Route key="ReviewPDF" component={ReviewPDFRoute} exact path="/license/licenseCopyPdf/" strict />,
    <Route key="PubEditorReview" component={PubEditorReviewRoute} exact path="/license/verification/" strict />,
    <Route key="LicenseSigning" component={LicenseSigningRoute} path="/license-signing/:articleId/:step?/:substep?" />,
    <Route key="Orders" component={OrdersRoute} exact path="/orders" strict />,
    <Route key="RequestOnlineOpenOrder" component={RequestOnlineOpenOrderRoute} exact path="/orders/request" />,
    <Route key="ViewOnlineOpenRequest" component={ViewOnlineOpenRequestRoute} exact path="/orders/view" />,
    <Route
        key="OrdersFocusOnArticle"
        component={OrdersFocusOnArticleRoute}
        exact
        path="/orders/focusOnArticle/:articleId"
        strict
    />,
    <Route key="Order" component={OrderRoute} exact path="/orders/:articleId" />,
    <Route key="AssignArticle" component={AssignArticleRoute} exact path="/assign-article" />,
    <Route key="EmailAlertsPage" component={EmailAlertsRoute} exact path="/alerts/:alertId" />,
    <Route key="NoMatch" component={NoMatchRoute} />,
];
